import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import PopupModal from "../../../common/PopupModal";
import CgFormCreator from "../../../common/CgFormCreator";
import {
  fetchPinDetails,
  fetchUserDetails,
  fetchAccessCodeDetails,
  searchAndValidatePractice,
  updateUserAccess,
  updateUserType,
  movePracticeUser,
  resendInvite,
  inviteUser,
} from "../../../store/ManageUser/manageUserActions";
import "./manageUsers.scss";
import {
  updateUserData,
  clearSearchPracticeDetails,
} from "../../../store/ManageUser/manageUserReducer";
import { useLocation } from "react-router-dom";
import { MyAccountContext } from "../../../App";
import { EMAIL_REGEX } from "../../../utils/commonUtilities";
import { fetchForgotPassword } from "../../../store/Login/LoginActions";

const ManageUsers = () => {
  const [openPopup, setOpenPopup] = useState(false);
  const [popupState, setPopupState] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [confirmRemoveMap, setConfirmRemoveMap] = useState({});
  const [activeFilter, setActiveFilter] = useState("GRANTED");
  const [practiceLookup, setPracticeLookup] = useState("");
  const [localUserDetails, setLocalUserDetails] = useState([]);
  const [showError, setShowError] = useState(false);
  const [showMsg, setShowMsg] = useState("");

  const dispatch = useDispatch();
  const { grantedRoles } = useContext(MyAccountContext) || { grantedRoles: [] };
  const isPractice = grantedRoles.includes("ROLE_PRACTICE");
  const isAdmin = grantedRoles.includes("ROLE_ADMIN");

  const pinDetails = useSelector((state) => state.pinDetails);
  const {
    userDetails,
    isLoadingUserDetails,
    errorUserDetails,
    errorUserDetailsMsg,
  } = useSelector((state) => state.userDetails);

  const accessCodeDetails = useSelector((state) => state.accessCodeDetails);
  const { searchPracticeDetails, errorSearchPractice, errorSearchPracticeMsg } =
    useSelector((state) => state.searchPracticeDetails);

  const { errorInviteUser, errorInviteUserMsg } = useSelector(
    (state) => state.inviteUser
  );

  const { errorUpdateUserType, errorUpdateUserTypeMsg } = useSelector(
    (state) => state.updateUserType
  );

  const { errorMovePractice, errorMovePracticeMsg } = useSelector(
    (state) => state.movePracticeDetails
  );

  const { errorResendInvite, errorResendInviteMsg } = useSelector(
    (state) => state.resendInvite
  );

  const { errorUserAccess, errorUserAccessMsg } = useSelector(
    (state) => state.userAccess
  );
  const { practiceStatusData } = useSelector((state) => state.practiceStatus);

  const { myaccountLogin } = useSelector(
    (state) => state.myaccountLoginInformation
  );

  const pinNumber = pinDetails.pinDetails[0];
  const accessCode = accessCodeDetails.accessCodeDetails?.[0]?.accessCode;

  const location = useLocation();
  const locationData = location.pathname.split("/");
  const practiceId = locationData[2];

  const { userDetailsError } = useSelector((state) => state.loginUserDetails);
  const [showErrMsg, setShowErrMsg] = useState("");

  useEffect(() => {
    setShowErrMsg(userDetailsError);
    setTimeout(() => {
      setShowErrMsg("")
    }, 2000);
  }, [userDetailsError]);

  useEffect(() => {
    dispatch(fetchPinDetails(practiceId));
    dispatch(fetchUserDetails(practiceId));
    dispatch(fetchAccessCodeDetails(practiceId));
  }, []);

  useEffect(() => {
    if (userDetails) {
      setLocalUserDetails(userDetails);
    }
  }, [userDetails]);

  useEffect(() => {
    const hasError =
      errorUserDetails ||
      errorSearchPractice ||
      errorInviteUser ||
      errorUpdateUserType ||
      errorMovePractice ||
      errorResendInvite ||
      errorUserAccess;

    if (hasError) {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  }, [
    errorUserDetails,
    errorSearchPractice,
    errorInviteUser,
    errorUpdateUserType,
    errorMovePractice,
    errorResendInvite,
    errorUserAccess,
  ]);

  const getFilteredUsers = () => {
    if (!Array.isArray(localUserDetails)) {
      return [];
    }
    if (activeFilter === "ALL" || isPractice) {
      return localUserDetails;
    }
    const filteredUsers = localUserDetails.filter((user) => {
      return user.access === activeFilter;
    });
    return filteredUsers;
  };
  const isSearchPracticeInactive =
    searchPracticeDetails?.practiceStatus === "INACTIVE";
  const isCurrentPracticeInactive = !practiceStatusData?.isActive;

  const manageAddUserSchema = [
    {
      component: "input",
      name: "firstName",
      label: "First Name",
      required: true,
      valueChangeCallback: () => {},
    },
    {
      component: "input",
      name: "email",
      label: "Email",
      type: "email",
      validateEmail: true,
      required: true,
      valueChangeCallback: () => {},
      inputProps: {
        pattern: EMAIL_REGEX,
      },
    },
    {
      component: "dropdown",
      name: "role",
      label: "Role",
      dropdownOptions: [
        { value: "Dentist", label: "Dentist" },
        { value: "Office Staff/Hygienist", label: "Office Staff/Hygienist" },
      ],
      onSelectCallBack: () => {},
      required: true,
      dropdownPlaceHolder: "Select",
    },
  ];

  const manageEditUserSchema = [
    {
      component: "input",
      name: "firstName",
      label: "First Name",
      required: true,
      valueChangeCallback: () => {},
      defaultValue: currentUser?.firstName || "",
    },
    {
      component: "input",
      name: "lastName",
      label: "Last Name",
      valueChangeCallback: () => {},
      defaultValue: currentUser?.lastName || "",
    },
    {
      component: "dropdown",
      name: "role",
      label: "Role",
      dropdownOptions: [
        { value: "Dentist", label: "Dentist" },
        { value: "Office Staff/Hygienist", label: "Office Staff/Hygienist" },
      ],
      onSelectCallBack: () => {},
      required: true,
      defaultValue:
        currentUser?.type === "STAFF" ? "Office Staff/Hygienist" : "Dentist",
    },
  ];

  const managemoveUserSchema = [
    {
      component: "input",
      type: "text",
      name: "practiceLookup",
      label: "",
      placeholder: "ENTER PRACTICE DISPLAY ID",
      required: true,
      valueChangeCallback: (name, value) =>
        setPracticeLookup(value.toUpperCase()),
      defaultValue: practiceLookup,
      customErrorMsg: errorSearchPractice
        ? "Unable to locate practice, please verify Practice Display ID and search again."
        : isSearchPracticeInactive
        ? "Users cannot be moved to INACTIVE practices."
        : undefined,
    },
    {
      component: "input",
      type: "button",
      name: "search",
      label: "Search",
      btnClassName: "btn edit-btn",
      handleButtonClickCallback: () => handleSearch(),
    },
  ];

  const handleSearch = () => {
    dispatch(clearSearchPracticeDetails());
    dispatch(
      searchAndValidatePractice({
        searchPracticeId: practiceLookup,
        currentPracticeId: currentUser.practiceId,
      })
    );
  };

  const handleMoveUser = async () => {
    try {
      if (!searchPracticeDetails || !currentUser) return;

      dispatch(
        movePracticeUser({
          userId: currentUser.userId,
          practiceId: searchPracticeDetails.displayId,
          currentPracticeId: practiceId,
        })
      )
        .then(() => {
          setLocalUserDetails((prevUsers) =>
            prevUsers.filter((user) => user.userId !== currentUser.userId)
          );
          closePopup();
          dispatch(fetchUserDetails(practiceId));
        })
        .catch((error) => {
          return error;
        });
    } catch (error) {
      return error;
    }
  };

  const handleResendInvite = (user) => {
    dispatch(
      resendInvite({
        userId: user.userId,
        practiceId: user.practiceId,
        firstName: user.firstName,
        username: user.username,
        type: user.type,
      })
    ).then(() => {
      dispatch(fetchUserDetails(practiceId));
    });
  };

  const handleResetPassword = (user) => {
    const data = {
      username: user?.username,
    };
    dispatch(fetchForgotPassword(data)).then(() => {
      dispatch(fetchUserDetails(practiceId));
      setShowMsg("Reset email sent successfully");
      setTimeout(() => {
        setShowMsg("");
      }, 2000);
    });
  };

  const handleSave = (data) => {
    if (popupState === "move") {
      handleMoveUser();
    } else if (popupState === "edit") {
      dispatch(
        updateUserType({
          practiceId: practiceId,
          userDisplayId: currentUser.userId,
          firstName: data.firstName,
          lastName: data.lastName || "",
          type: data.role === "Dentist" ? "DENTIST" : "STAFF",
          accessType: "GRANTED",
        })
      )
        .then(() => {
          dispatch(
            updateUserData({
              ...currentUser,
              firstName: data.firstName,
              lastName: data.lastName || "",
              type: data.role === "Dentist" ? "DENTIST" : "STAFF",
            })
          );
          closePopup();
          dispatch(fetchUserDetails(practiceId));
        })
        .catch((error) => {
          return error;
        });
    } else {
      dispatch(
        inviteUser({
          practiceId: practiceId,
          firstName: data.firstName,
          username: data.email,
          type: data.role === "Dentist" ? "DENTIST" : "STAFF",
        })
      )
        .then(() => {
          dispatch(fetchUserDetails(practiceId));
          closePopup();
        })
        .catch((error) => {
          return error;
        });
    }
  };

  const closePopup = () => {
    setOpenPopup(false);
    setPopupState("");
    setCurrentUser(null);
    setPracticeLookup("");
    dispatch(clearSearchPracticeDetails());
  };

  const handleEditClick = (user) => {
    setCurrentUser(user);
    setPopupState("edit");
    setOpenPopup(true);
  };

  const handleMoveClick = (user) => {
    setCurrentUser(user);
    setPopupState("move");
    setOpenPopup(true);
  };
  const toggleConfirmRemove = (userId, value) => {
    if (!value) {
      setConfirmRemoveMap((prev) => {
        const newMap = { ...prev };
        delete newMap[userId];
        return newMap;
      });
    } else {
      setConfirmRemoveMap({
        [userId]: value,
      });
    }
  };

  const handleRemoveAccess = (user) => {
    dispatch(
      updateUserAccess({
        practiceId: practiceId,
        userDisplayId: user.userId,
        accessType: "REMOVED",
      })
    )
      .then(() => {
        dispatch(
          updateUserData({
            ...user,
            access: "REMOVED",
          })
        );
        toggleConfirmRemove(user.userId, false);
        dispatch(fetchUserDetails(practiceId));
      })
      .catch((error) => {
        return error;
      });
  };

  const RenderInactivePractice = () => {
    return (
      <div className="noDataInformationWrapper">
        Users cannot be invited to INACTIVE practices.
      </div>
    );
  };
  const RenderMoveUserForm = () => {
    return (
      <div className="move-user-container">
        <div className="move-userForm">
          <div className="practice-User-Details">
            <div className="user-details-section">
              <div className="user-info-card">
                <p className="practice-user-title">Practice User Details</p>
                <div className="user-info-item">
                  <div className="info-label">FULL NAME</div>
                  <div className="info-value">
                    {currentUser?.firstName} {currentUser?.lastName}
                  </div>
                </div>
                <div className="user-info-item">
                  <div className="info-label">USERNAME/EMAIL</div>
                  <div className="info-value">{currentUser?.username}</div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="move-form-container">
              <p>Move To Practice</p>
              <p className="field-title">Practice Lookup</p>
              <div className="row">
                <CgFormCreator
                  schema={managemoveUserSchema}
                  noSubmit={true}
                  formClassName="manageMoveUserForm"
                />
              </div>

              {/* {errorSearchPractice && ( */}
              {/* //   <div className="error-message">{errorSearchPracticeMsg}</div> */}
              {/* // )} */}

              {searchPracticeDetails && !isSearchPracticeInactive ? (
                <div className="search-result-section">
                  <div className="item1">
                    <label>Practice Name</label>
                    <p className="searchPracticeName">
                      {searchPracticeDetails.name}
                    </p>
                  </div>
                  <div className="item2">
                    <label>Phone</label>
                    <p className="searchPracticeName">
                      {searchPracticeDetails.phoneNumber}
                    </p>
                  </div>
                  <div className="item3">
                    <label>Address</label>
                    <p className="searchPracticeName">
                      {searchPracticeDetails.street1},{" "}
                      {searchPracticeDetails.street2},{" "}
                      {searchPracticeDetails.city},{" "}
                      {searchPracticeDetails.state}, {searchPracticeDetails.zip}
                    </p>
                  </div>
                </div>
              ) : null}

              <CgFormCreator
                schema={[]}
                primaryButton="MOVE USER"
                secondaryButton="CANCEL"
                onSubmitCallback={handleMoveUser}
                onCancel={closePopup}
                formClassName="moveUserActions"
                enablePrimaryBtn={
                  !!searchPracticeDetails && !isSearchPracticeInactive
                }
                noSubmit={false}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RenderFormCreator = () => {
    return (
      <CgFormCreator
        schema={
          popupState === "edit" ? manageEditUserSchema : manageAddUserSchema
        }
        onSubmitCallback={handleSave}
        onCancelCallback={closePopup}
        primaryButton={popupState === "edit" ? "Save" : "Invite"}
        secondaryButton="Cancel"
        needFormHeader={false}
        formClassName="manageUserFormAdd"
        onCancel={() => {
          closePopup();
        }}
        canFormCreatorValidateTheForm={popupState === "add"}
      />
    );
  };

  const getModalWidth = () => {
    if (popupState === "move") {
      return "83%";
    } else if (popupState === "edit") {
      return "70%";
    } else {
      return "70%";
    }
  };

  const RenderPopup = () => {
    if (!openPopup) {
      return null;
    }

    return (
      <PopupModal
        isOpen={openPopup}
        onClose={closePopup}
        onSave={handleSave}
        popupHeading={
          popupState === "edit"
            ? "Edit User"
            : popupState === "move"
            ? "Move User"
            : "Add User"
        }
        customRender={() => {
          if (popupState === "move") {
            return <RenderMoveUserForm />;
          } else if (isCurrentPracticeInactive) {
            return RenderInactivePractice();
          } else {
            return RenderFormCreator();
          }
        }}
        enableSave={false}
        width={getModalWidth()}
        className={`manageUser ${
          isCurrentPracticeInactive ? "inactivePractice" : ""
        }`}
      />
    );
  };

  const RenderErrors = () => {
    if (!showError) return null;

    let errorMessage = "";

    if (errorUserDetails) errorMessage = errorUserDetailsMsg;
    else if (errorSearchPractice) errorMessage = errorSearchPracticeMsg;
    else if (errorInviteUser) errorMessage = errorInviteUserMsg;
    else if (errorUpdateUserType) errorMessage = errorUpdateUserTypeMsg;
    else if (errorMovePractice) errorMessage = errorMovePracticeMsg;
    else if (errorResendInvite) errorMessage = errorResendInviteMsg;
    else if (errorUserAccess) errorMessage = errorUserAccessMsg;

    return (
      <div className="alert alert-danger" role="alert">
        {errorMessage}
      </div>
    );
  };

  const RenderRemoveAccess = (user) => {
    if (myaccountLogin.user?.username === user?.username) return null;

    if (confirmRemoveMap[user.userId]) {
      return (
        <>
          <a
            className="userLinks"
            onClick={() => toggleConfirmRemove(user.userId, false)}
          >
            CANCEL
          </a>
          <a
            className="deleteConfirmation"
            onClick={() => handleRemoveAccess(user)}
          >
            CONFIRM
          </a>
        </>
      );
    } else {
      return (
        <a
          className="userLastLink"
          onClick={() => toggleConfirmRemove(user.userId, true)}
        >
          REMOVE ACCESS
        </a>
      );
    }
  };

  const RenderManageUsers = () => {
    if (isLoadingUserDetails) {
      return <div>Loading users...</div>;
    }

    const filteredUsers = getFilteredUsers();

    return (
      <div className="manage-users-container">
        <div className="successErrorMsg">
          {showMsg && (
            <div className="alert alert-success" role="alert">
              <p>{showMsg}</p>
              <div onClick={() => setShowMsg("")}>x</div>
            </div>
          )}
        </div>
        <div className="successErrorMsg">
          {showErrMsg && (
            <div className="alert alert-danger" role="alert">
              <p>{showErrMsg}</p>
              <div onClick={() => setShowErrMsg("")}>x</div>
            </div>
          )}
        </div>
        <div className="manageUsersHeader">
          <h3 className="section-header top-thirty overview">Manage Users</h3>
        </div>
        <div className="mangeUsers"></div>
        {showError && <RenderErrors />}
        <div className="manage-first-block">
          <div className="pin-block">
            <span className="manageUsersPin">PIN # {pinNumber}</span>
            <span className="mangeUserAccessCode">
              Access Code : {accessCode}
            </span>
          </div>
          {!isPractice && (
            <div className="filters-options">
              <a
                className={`filterUserLinks ${
                  activeFilter === "GRANTED" ? "active" : ""
                }`}
                onClick={() => setActiveFilter("GRANTED")}
              >
                GRANTED
              </a>
              <a
                className={`filterUserLinks ${
                  activeFilter === "REMOVED" ? "active" : ""
                }`}
                onClick={() => setActiveFilter("REMOVED")}
              >
                REMOVED
              </a>
              <a
                className={`filterUserLastLinks ${
                  activeFilter === "ALL" ? "active" : ""
                }`}
                onClick={() => setActiveFilter("ALL")}
              >
                ALL
              </a>
            </div>
          )}
        </div>

        <div className="manageUserscontainer display-flex">
          <div className="col-md-12">
            {filteredUsers.length === 0 ? (
              <div className="no-users-message info-section ng-isolate-scope mx-3">
                <p className="empty-module">
                  No practice users for this practice
                </p>
              </div>
            ) : (
              filteredUsers?.map((user, index) => (
                <div
                  key={user.id || index}
                  className="container manageUsersDataCard"
                >
                  <div className="row manageUsersCardRow">
                    <div className="col-12 border manageUsersRowHeight">
                      <div className="manageUsersData">
                        <p className="userName">
                          USER : {user.firstName} {user.lastName}
                        </p>
                        <>
                          <div className="manageUsersData1">
                            <div className="manageUsersAction">
                              {user.access === "GRANTED" && (
                                <>
                                  <a
                                    className="userLinks"
                                    onClick={() => handleEditClick(user)}
                                  >
                                    EDIT
                                  </a>
                                  {user.confirmed === true ? (
                                    <a
                                      className="userLinks"
                                      onClick={() => handleResetPassword(user)}
                                    >
                                      SEND PASSWORD RESET EMAIL
                                    </a>
                                  ) : (
                                    <a
                                      className="userLinks"
                                      onClick={() => handleResendInvite(user)}
                                    >
                                      RESEND INVITE
                                    </a>
                                  )}
                                  {(isAdmin &&
                                    !isPractice &&
                                    user?.username !==
                                      myaccountLogin.user?.username ) ? (
                                      <a
                                        className="userLinks"
                                        onClick={() => handleMoveClick(user)}
                                      >
                                        MOVE USER
                                      </a>
                                    ): null}
                                  {RenderRemoveAccess(user)}
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      </div>
                    </div>

                    <div className="col-4 border">
                      <div className="table-content">
                        <p className="header">Role</p>
                        <p className="header-1">
                          {user.type ? (
                            user.type
                          ) : (
                            <>
                              <a
                                className="userLinks no-border-right"
                                onClick={() => handleEditClick(user)}
                              >
                                ADD ROLE
                              </a>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="col-4 border">
                      <div className="table-content">
                        <p className="header">Email</p>
                        <p className="headerEmail">{user.username}</p>
                      </div>
                    </div>
                    <div className="col-4 border">
                      <div className="table-content">
                        <p className="header">Confirmation Date</p>
                        <p className="headerConfirmation">
                          {user.confirmed === true
                            ? user?.confirmationDate
                            : "Unconfirmed"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        <a
          className="btn edit-button manageAddbtn"
          onClick={() => {
            setOpenPopup(true);
            setPopupState("add");
          }}
        >
          +ADD USER
        </a>
      </div>
    );
  };

  return (
    <div className={`manage-user-wrapper popup-state-${popupState}`}>
      {RenderManageUsers()}
      {RenderPopup()}
      {RenderErrors()}
    </div>
  );
};

export default ManageUsers;
